import { ReactElement, useEffect, useState } from "react";
import { IChartWrapperProps } from "../../types";
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { getComparator, stableSort } from "../../../../system/utils";
import { IColumn, Order } from "../../../../types";
import { getAggregationText, getSchema } from "../utils";
import { formats } from "../../../../system/formats";
import { converters } from "../../../../system/converters";
import TableContainer from "../../../../components/tableContainer";
import { styled } from "@mui/material/styles";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const SummaryWrapper = ({ cell, data, parentSize, schemas }: IChartWrapperProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("beginDatetime");
  const [columns, setColumns] = useState<IColumn[]>([]);

  useEffect(() => {
    const schema = getSchema(schemas, cell.module);

    const cols: IColumn[] = [{
      id: cell.dimension,
      label: cell.dimension,
      minWidth: 100,
      isHeader: true,
      format: formats[schema.dimensions[cell.dimension].formatName]
    }];

    cell.groups.forEach(g => {
      cols.push({
        id: g.field,
        label: g.field,
        minWidth: 100,
        format: formats[schema.dimensions[g.field].formatName]
      });
    });

    cell.variables.forEach(s => {
      const d = schema.variables[s.field];
      let label = s.unit ? <span>{`${s.field} (${getAggregationText(s.aggregate)})`}<sub>{` [${s.unit}]`}</sub></span> : `${s.field} (${getAggregationText(s.aggregate)})`;
      if (s.custom && s.custom.title !== "") {
        // label = s.unit ? <span>{`${s.custom.title}`}<sub>{` [${s.unit}]`}</sub></span> : `${s.custom.title}`;
        label = s.custom.title;
      }

      cols.push({
        id: s.field,
        label,
        minWidth: 100,
        align: s.custom ? s.custom.align : "left",
        format: (value: number): string => converters[d.converterName](value, s.unit).toFixed(s.decimals)
      });
    });

    setOrder(cell.orderAsc ? "asc" : "desc");
    setOrderBy(cell.filterBy === "DIMENSION" ? cell.dimension : cell.variables[0].field);
    setColumns(cols);
  }, [ cell ]);


  const createSortHandler = (property: string) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <div className="display-flex-row" style={{ ...parentSize }}>
      <TableContainer>
        <Table stickyHeader={true} size="small">
          <TableHead>
            <TableRow className="p-1">
              {columns.map((column) =>
                <TableCell
                  className="table-header"
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth, fontSize: `${cell.style ? cell.style.scale ?? 1 : 1 }em`, lineHeight: "initial"  }}
                  sortDirection={orderBy === column.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={createSortHandler(column.id)}>
                    {column.label}
                    {orderBy === column.id
                      ? <span className="cell-visuallyHidden">
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </span>
                      : null}
                  </TableSortLabel>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(data.result, getComparator(order, orderBy))
              .slice(0, cell.top > 0 ? cell.top : data.result.length)
              .map((row: any, index: number) => {
                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}>
                    {columns.map((column: IColumn) => {
                      const value = row[column.id];

                      return (
                        <TableCell key={column.id} align={column.align} sx={{ fontSize: `${cell.style ? cell.style.scale ?? 1 : 1 }em`  }} className={column.isHeader ? "table-header" : ""}>
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })}
            <TableRow className="p-1">
              {data.totals && columns.map((column) => {
                const value = data.totals ? data.totals[column.id] : null;

                return (
                  <TableCell
                    className="table-header"
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      fontSize: `${cell.style ? cell.style.scale ?? 1 : 1}em`,
                      lineHeight: "initial"
                    }}
                    sortDirection={orderBy === column.id ? order : false}>
                    {value && column.format && typeof value === "number" ? column.format(value) : value ?? ""}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SummaryWrapper;
